import apiClient from 'api/base';
import type { HomePageWidgetConfig } from 'pages/HomePage/constants';
import {
  mapWidgetKeys_backendToLocal,
  mapWidgetKeys_localToBackend,
} from './appSettings.helpers';
import { BroadcastTag } from '../models/broadcastTag.model';

export type GridLayout = {
  cell_0_0: string | null;
  cell_0_1: string | null;
  cell_1_0: string | null;
  cell_1_1: string | null;
};

export type WidgetDefinition = {
  id: string;
  name: string;
  icon?: string;
  description?: string;
};

export type UserHomePageSettingsResponse = {
  grid: {
    rows: number;
    cols: number;
  };
  widgets: WidgetDefinition[];
  layout: GridLayout;
  isCustomized: boolean;
  configVersion: number;
};

export type SaveUserHomePageSettingsRequest = {
  layout: GridLayout;
};

export type BroadcastTagsResponse = BroadcastTag[];

export type CreateBroadcastTagPayload = {
  name: string;
  order: number;
};

export type UpdateBroadcastTagPayload = {
  name: string;
};

/**
 * Returns user's saved layout (merged with the current global config defaults)
 * GET /admins/me/settings/home-page
 */
export const getUserHomePageSettings = async (): Promise<
  Omit<UserHomePageSettingsResponse, 'layout'> & {
    layout: HomePageWidgetConfig;
  }
> => {
  const { data } = await apiClient.get<UserHomePageSettingsResponse>(
    '/admins/me/settings/home-page',
  );

  return { ...data, layout: mapWidgetKeys_backendToLocal(data.layout) };
};

/**
 * Saves custom layout for the current admin
 * PUT /admins/me/settings/home-page
 */
export const saveUserHomePageSettings = async (
  widgetsLayout: HomePageWidgetConfig,
): Promise<void> => {
  const payload: SaveUserHomePageSettingsRequest = {
    layout: mapWidgetKeys_localToBackend(widgetsLayout),
  };

  await apiClient.put('/admins/me/settings/home-page', payload);
};

/**
 * Resets user's layout to the global default for their role.
 * DELETE /admins/me/settings/home-page
 */
export const resetUserHomePageSettings = async (): Promise<void> => {
  await apiClient.delete('/admins/me/settings/home-page');
};

/**
 * Returns available broadcast tags
 * GET /tags
 */
export const getBroadcastTags = async (): Promise<BroadcastTagsResponse> => {
  const { data } = await apiClient.get<BroadcastTagsResponse>('/tags');

  return data;
};

/**
 * Create new broadcast tag
 * POST /tags
 */
export const createBroadcastTag = async (
  payload: CreateBroadcastTagPayload,
): Promise<BroadcastTag> => {
  const { data } = await apiClient.post<BroadcastTag>('/tags', {
    name: payload.name,
    order: payload.order,
  });

  return data;
};

/**
 * Update a broadcast tag
 * PUT /tags
 */
export const updateBroadcastTag = async (
  id: string,
  payload: UpdateBroadcastTagPayload,
): Promise<BroadcastTag> => {
  const { data } = await apiClient.put<BroadcastTag>(`/tags/${id}`, {
    name: payload.name,
  });

  return data;
};

/**
 * Delete a broadcast tag
 * DELETE /tags
 */
export const deleteBroadcastTag = async (id: string): Promise<BroadcastTag> => {
  const { data } = await apiClient.delete<BroadcastTag>(`/tags/${id}`);

  return data;
};
